import { Currency } from '@moment/trading-types/reference-data'

import { intrinsicScopeForConfig } from '@/portfolio/proposal-builder/config'
import { DEFAULT_CASHFLOW_TARGET_PRIORITY } from '@/portfolio/proposal-builder/data/helpers/parse-priority'
import { createEmptyDiversificationParameterConstraints } from '@/portfolio/proposal-builder/data/transformers/diversification-parameter-constraints'
import { createDefaultGlobalConstraints } from '@/portfolio/proposal-builder/data/transformers/global-constraints'
import {
  type ConfigKey,
  type Field,
  type FieldConstraints,
  type FilterListConstraints,
  Priority,
  type RiskFactor,
  type Sleeve,
  type SleeveID,
  type SleeveState,
} from '@/portfolio/proposal-builder/types'
import { RebalanceOptions } from '@/types/enums/sleeve-management'

export const DEFAULT_SLEEVE_ID = 'slv_default'

export const checkSleevesLoaded = (sleeves: Sleeve[]): boolean => {
  return (
    sleeves.length > 0 &&
    sleeves.every((sleeve) => sleeve.sleeveId !== DEFAULT_SLEEVE_ID)
  )
}

/**
 * Generate a unique ID for sleeves and risk factors
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Deep clone a sleeve state
 */
export function cloneSleeveState(state: SleeveState): SleeveState {
  return {
    diversificationParameterConstraints: JSON.parse(
      JSON.stringify(
        state.diversificationParameterConstraints ||
          createEmptyDiversificationParameterConstraints(),
      ),
    ),
    riskFactors: Object.fromEntries(
      Object.entries(state.riskFactors).map(([id, riskFactor]) => [
        id,
        cloneRiskFactor(riskFactor),
      ]),
    ),
    cashflowTargets: JSON.parse(JSON.stringify(state.cashflowTargets)),
    cashflowTargetPriority: state.cashflowTargetPriority,
    rebalanceStrategy: state.rebalanceStrategy,
    otherConstraints: JSON.parse(JSON.stringify(state.otherConstraints)),
    objectiveTokens: JSON.parse(JSON.stringify(state.objectiveTokens)),
    globalConstraints: JSON.parse(JSON.stringify(state.globalConstraints)),
    cashBuffer: state.cashBuffer ?? 0,
    minimum_order_increment: state.minimum_order_increment ?? null,
    scopeRelativeWeights: state.scopeRelativeWeights,
  }
}

/**
 * Deep clone a risk factor
 */
function cloneRiskFactorFields(
  fields: RiskFactor['fields'],
): RiskFactor['fields'] {
  return Object.fromEntries(
    Object.entries(fields).map(([field, config]) => [
      field,
      {
        universeConstraint: config.universeConstraint
          ? { ...config.universeConstraint }
          : undefined,
        diversificationConstraint: config.diversificationConstraint
          ? { ...config.diversificationConstraint }
          : undefined,
        piecewiseDiversificationConstraint:
          config.piecewiseDiversificationConstraint
            ? { ...config.piecewiseDiversificationConstraint }
            : undefined,
        averageValueConstraint: config.averageValueConstraint
          ? { ...config.averageValueConstraint }
          : undefined,
        allocationConstraints: [...config.allocationConstraints],
      },
    ]),
  ) as Partial<Record<Field, FieldConstraints>>
}

function cloneRiskFactor(riskFactor: RiskFactor): RiskFactor {
  return {
    configKey: riskFactor.configKey,
    priorityLevel: riskFactor.priorityLevel,
    scope: { ...riskFactor.scope },
    fields: cloneRiskFactorFields(riskFactor.fields),
  }
}

/**
 * Create an empty sleeve state. Risk factors start empty and are seeded at
 * proposal load.
 */
function createEmptySleeveState(currency?: Currency): SleeveState {
  return {
    diversificationParameterConstraints:
      createEmptyDiversificationParameterConstraints(),
    riskFactors: {},
    cashflowTargets: [],
    cashflowTargetPriority: DEFAULT_CASHFLOW_TARGET_PRIORITY,
    rebalanceStrategy: RebalanceOptions.Never,
    otherConstraints: [],
    objectiveTokens: [],
    globalConstraints: createDefaultGlobalConstraints(currency),
    cashBuffer: 0,
    minimum_order_increment: null,
  }
}
/**
 * Create a new sleeve with empty state
 * @param sleeveId - The unique identifier for the sleeve
 * @param name - Display name for the sleeve (defaults to sleeveId)
 * @param currency - The sleeve's currency (defaults to USD)
 */
export function createSleeve(
  sleeveId: SleeveID,
  name?: string,
  currency: Currency = Currency.USD,
): Sleeve {
  return {
    sleeveId: sleeveId,
    name: name ?? sleeveId,
    state: createEmptySleeveState(currency),
    minAllocation: 0,
    targetAllocation: 1,
    maxAllocation: 1,
  }
}

/**
 * Create an empty field constraints for a field
 */
export function createEmptyFieldConstraints(): FieldConstraints {
  return {
    universeConstraint: undefined,
    diversificationConstraint: undefined,
    piecewiseDiversificationConstraint: undefined,
    averageValueConstraint: undefined,
    allocationConstraints: [],
  }
}

/**
 * Create empty filter list constraints
 */
export function createEmptyFilterListConstraints(): FilterListConstraints {
  return {
    inclusionConstraint: undefined,
    exclusionConstraint: undefined,
    allocationConstraints: [],
  }
}

/**
 * Create a new risk factor with default values
 */
export function createRiskFactor(
  configKey: ConfigKey,
  priorityLevel: Priority = Priority.Low,
): RiskFactor {
  return {
    configKey: configKey,
    priorityLevel: priorityLevel,
    scope: intrinsicScopeForConfig(configKey),
    fields: {},
  }
}
